import * as XLSX from 'xlsx-js-style';
import { UT_TYPES } from '../constants';
import { AnalysisRow, UtType } from '../types';
import { aggregateByProcessEqGroup, calcTotals } from './analyze';

/**
 * @param utTypes 산출할 UT 항목 — 화면과 같은 목록·순서를 쓰려고 호출한 쪽에서 넘긴다
 *   (공통코드 MECH_UT_CTGR_CD 기준). 넘기지 않으면 상수 전체를 쓴다.
 * @param procOrder 공정 표시 순서(공통코드 PRDN_PROC_CD 기준, `공정명 → 순번`).
 *   세부 Summary 시트를 화면 ⑤와 같은 순서로 맞추는 데 쓴다.
 *   (Machine 행 순서는 호출부가 미리 정렬해 rows로 넘긴다)
 */
export function exportExcel(
  rows: AnalysisRow[],
  versionLabels: string[],
  isPerLineMode: boolean,
  utTypes: UtType[] = UT_TYPES,
  procOrder: Map<string, number> = new Map()
): void {
  const wbOut = XLSX.utils.book_new();
  const n = versionLabels.length;
  const vNums = Array.from({ length: n }, (_, i) => i + 1);

  for (const ut of utTypes) {
    // 차이 여부와 무관하게 항상 모든 매칭 Machine(+Main) 포함
    const wsData: (string | number)[][] = [];

    if (isPerLineMode) {
      wsData.push([
        '공정',
        '설비군',
        '세부공정',
        'Main',
        'Machine',
        ...vNums.map((v) => `V${v} 사용량/라인`),
        '사용량 차이',
        ...vNums.map((v) => `V${v} Q'ty/라인`),
        "Q'ty 차이",
        '차이여부',
      ]);

      const tot = { usage: Array(n).fill(0), diffU: 0, qty: Array(n).fill(0), diffQ: 0 };
      for (const r of rows) {
        const d = r.ut[ut.key];
        if (!d) continue;
        wsData.push([
          r.process,
          r.eqGroup,
          r.subProcess,
          r.main,
          r.machine,
          ...d.usage,
          d.diffU,
          ...d.qty,
          d.diffQ,
          r.machineHasDiff ? '차이있음' : '동일',
        ]);
        d.usage.forEach((v, i) => (tot.usage[i] += v));
        tot.diffU += d.diffU;
        d.qty.forEach((v, i) => (tot.qty[i] += v));
        tot.diffQ += d.diffQ;
      }

      if (rows.length > 0) {
        wsData.push(['합계', '', '', '', '', ...tot.usage, tot.diffU, ...tot.qty, tot.diffQ, '']);
      }

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [
        12,
        12,
        10,
        10,
        16,
        ...Array(n).fill(14),
        14,
        ...Array(n).fill(12),
        12,
        10,
      ].map((w) => ({ wch: w }));
      const sheetName = ut.key.length > 31 ? ut.key.slice(0, 31) : ut.key;
      XLSX.utils.book_append_sheet(wbOut, ws, sheetName);
    } else {
      wsData.push([
        '공정',
        '설비군',
        '세부공정',
        'Machine',
        '라인',
        ...vNums.map((v) => `V${v} 총사용량`),
        '총사용량 차이',
        ...vNums.map((v) => `V${v} 단위사용량`),
        '단위사용량 차이',
        ...vNums.map((v) => `V${v} Q'ty`),
        "Q'ty 차이",
        '차이여부',
      ]);

      const tot = {
        usage: Array(n).fill(0),
        diffU: 0,
        unit: Array(n).fill(0),
        qty: Array(n).fill(0),
        diffQ: 0,
      };
      let cnt = 0;
      for (const r of rows) {
        const d = r.ut[ut.key];
        if (!d) continue;
        const diffUnit = d.unit.length > 0 ? d.unit[d.unit.length - 1] - d.unit[0] : 0;
        wsData.push([
          r.process,
          r.eqGroup,
          r.subProcess,
          r.machine,
          r.line,
          ...d.usage,
          d.diffU,
          ...d.unit,
          diffUnit,
          ...d.qty,
          d.diffQ,
          r.machineHasDiff ? '차이있음' : '동일',
        ]);
        d.usage.forEach((v, i) => (tot.usage[i] += v));
        tot.diffU += d.diffU;
        d.unit.forEach((v, i) => (tot.unit[i] += v));
        d.qty.forEach((v, i) => (tot.qty[i] += v));
        tot.diffQ += d.diffQ;
        cnt++;
      }

      if (rows.length > 0) {
        const unitAvg = tot.unit.map((v) => (cnt ? v / cnt : 0));
        const diffUnitTot = unitAvg.length > 0 ? unitAvg[unitAvg.length - 1] - unitAvg[0] : 0;
        wsData.push([
          '합계',
          '',
          '',
          '',
          '',
          ...tot.usage,
          tot.diffU,
          ...unitAvg,
          diffUnitTot,
          ...tot.qty,
          tot.diffQ,
          '',
        ]);
      }

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [
        12,
        12,
        10,
        16,
        8,
        ...Array(n).fill(14),
        14,
        ...Array(n).fill(14),
        14,
        ...Array(n).fill(10),
        10,
        10,
      ].map((w) => ({ wch: w }));
      const sheetName = ut.key.length > 31 ? ut.key.slice(0, 31) : ut.key;
      XLSX.utils.book_append_sheet(wbOut, ws, sheetName);
    }
  }

  // PPT Summary 시트 (perLine 모드: Main+Machine별 라인당 사용량 합산 기준)
  const totals = calcTotals(rows, n);
  const sumData: (string | number)[][] = [
    ['버전' + (isPerLineMode ? ' (라인당 사용량 합산)' : ''), ...utTypes.map((u) => u.label)],
  ];
  versionLabels.forEach((name, vi) => {
    sumData.push([name, ...utTypes.map((ut) => totals[ut.key][vi])]);
  });
  sumData.push([
    '차이',
    ...utTypes.map((ut) => {
      const arr = totals[ut.key];
      return arr[arr.length - 1] - arr[0];
    }),
  ]);
  XLSX.utils.book_append_sheet(wbOut, XLSX.utils.aoa_to_sheet(sumData), 'PPT Summary');

  // 세부 Summary 시트 (공정/설비군/세부공정별 UT 사용량 집계)
  const detailGroups = aggregateByProcessEqGroup(rows, n, procOrder);
  const detailHeader: (string | number)[] = ['공정', '설비군', '세부공정'];
  for (const ut of utTypes) {
    detailHeader.push(
      ...vNums.map((v) => `${ut.label} V${v}`),
      `${ut.label} 차이`,
      `${ut.label} 증감률(%)`
    );
  }
  const detailData: (string | number)[][] = [detailHeader];
  const buildDetailRow = (usage: number[]): (string | number)[] => {
    const diff = usage[usage.length - 1] - usage[0];
    const pct = usage[0] === 0 ? '' : Number(((diff / usage[0]) * 100).toFixed(1));
    return [...usage, diff, pct];
  };
  for (const g of detailGroups) {
    const row: (string | number)[] = [g.process, g.eqGroup, g.subProcess];
    for (const ut of utTypes) row.push(...buildDetailRow(g.ut[ut.key].usage));
    detailData.push(row);
  }
  const detailTotalRow: (string | number)[] = ['합계', '', ''];
  for (const ut of utTypes) {
    const usage = Array(n).fill(0);
    for (const g of detailGroups) g.ut[ut.key].usage.forEach((v, i) => (usage[i] += v));
    detailTotalRow.push(...buildDetailRow(usage));
  }
  detailData.push(detailTotalRow);
  const wsDetail = XLSX.utils.aoa_to_sheet(detailData);
  wsDetail['!cols'] = [16, 16, 12, ...Array(utTypes.length * (n + 2)).fill(12)].map((w) => ({
    wch: w,
  }));
  XLSX.utils.book_append_sheet(wbOut, wsDetail, '세부 Summary');

  XLSX.writeFile(wbOut, 'UT_Comparison_Result.xlsx');
}
